import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import {
  Center,
  Column,
  ErrorText,
  LoadingIndicator,
  TableViews,
} from "@/components";
import { MyAviaryCandidateSheet } from "@/components/my-aviary/MyAviaryCandidateSheet";
import { MyAviaryTable } from "@/components/my-aviary/MyAviaryTable";
import { MyAviaryTableQueryOptions } from "@/components/my-aviary/MyAviaryTableQueryOptions";
import {
  ALL_USERS_KEY,
  PendingSelection,
} from "@/components/my-aviary/MyAviaryUserSelector";

import { useDefaultViewGuard } from "@/hooks/useDefaultViewGuard";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useSelectedResourceIdFromUrl } from "@/hooks/useSelectedResourceIdFromUrl";
import { useSetTitle } from "@/hooks/useSetTitle";
import { useUpdateView } from "@/hooks/mutations/useUpdateView";
import { useGetJobAndCompanyIdForCandidacy } from "@/hooks/queries/useGetJobAndCompanyIdForCandidacy";
import { useGetMyCandidacies } from "@/hooks/queries/useGetMyCandidacies";
import { useGetViews } from "@/hooks/queries/useGetViews";
import { useSetView } from "@/hooks/sets/useSetView";
import { aviary } from "@/lib/aviary";
import { defaultMyCandidaciesQueryParams } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { mutations } from "@/lib/mutations";
import { aviaryStore } from "@/stores/aviary";
import { MyCandidaciesQueryParams, View, ViewTableName } from "@/types";

const viewQueryParams = {
  tableName: ViewTableName.MY_CANDIDACIES,
};

export const MyAviaryPage = () => {
  const [selectedView, setSelectedView] = useState<View | null>(null);
  const [nextCandidacyId, setNextCandidacyId] = useState<string | null>(null);
  const [previousCandidacyId, setPreviousCandidacyId] = useState<string | null>(
    null,
  );

  const lastViewId = aviaryStore((s) => s.lastViewId);
  const setLastViewId = aviaryStore((s) => s.setLastViewId);

  // Spread defaults first so views saved before new query param fields were
  // added still have valid values for any newly added fields.
  const {
    data: myCandidacies,
    isLoading: isMyCandidaciesLoading,
    isError: isMyCandidaciesError,
    isSuccess: isMyCandidaciesSuccess,
  } = useGetMyCandidacies({
    ...defaultMyCandidaciesQueryParams,
    ...((selectedView?.queryParams as MyCandidaciesQueryParams | null) ?? {}),
  });

  // User selector is fully controlled. Click → spinner shows next to selected
  // option, dropdown stays open. When fresh data lands, dropdown closes.
  const [userSelectorOpen, setUserSelectorOpen] = useState(false);
  const [userSelectorPending, setUserSelectorPending] =
    useState<PendingSelection | null>(null);
  // Snapshot of the candidacies array at click time so we can detect when
  // fresh data has replaced the placeholder.
  const dataAtClickRef = useRef(myCandidacies);
  const {
    data: views,
    isLoading: isViewsLoading,
    isError: isViewsError,
    isSuccess: isViewsSuccess,
  } = useGetViews({
    tableName: ViewTableName.MY_CANDIDACIES,
  });

  const { mutate: updateView } = useUpdateView();
  const setView = useSetView();

  const navigate = useNavigate();
  const errorToast = useErrorToast();

  const {
    selectedResourceId: selectedCandidacyIdRaw,
    setSelectedResourceId: setSelectedCandidacyId,
  } = useSelectedResourceIdFromUrl({ paramName: "c" });
  // useSelectedResourceIdFromUrl returns number | string | null. Candidacy ids
  // are UUIDs, so coerce to string.
  const selectedCandidacyId =
    typeof selectedCandidacyIdRaw === "string" ? selectedCandidacyIdRaw : null;

  useSetTitle({ title: "My Aviary" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.MY_AVIARY);
  }, []);

  useDefaultViewGuard({
    views,
    selectedView,
    setSelectedView,
    tableName: ViewTableName.MY_CANDIDACIES,
    defaultQueryParams: defaultMyCandidaciesQueryParams,
    preferredViewId: lastViewId,
  });

  // Persist the selected view so the user lands back on it after refreshing
  // or returning from the candidate sheet / pipeline.
  useEffect(() => {
    if (selectedView) setLastViewId(selectedView.id);
  }, [selectedView, setLastViewId]);

  const myCandidaciesQueryParams: MyCandidaciesQueryParams = {
    ...defaultMyCandidaciesQueryParams,
    ...((selectedView?.queryParams as MyCandidaciesQueryParams | null) ?? {}),
  };

  const handleUpdateView = (queryParams: MyCandidaciesQueryParams) => {
    if (!selectedView) return;

    mutations.updateView({
      setSelectedView,
      setView,
      updateView,
      updateViewRequest: { queryParams },
      viewId: selectedView.id,
      viewQueryParams,
    });
  };

  // When fresh data has replaced the placeholder (ref differs from the
  // click-time snapshot), clear the pending state and close the dropdown.
  useEffect(() => {
    if (userSelectorPending === null) return;
    if (myCandidacies === dataAtClickRef.current) return;
    setUserSelectorPending(null);
    setUserSelectorOpen(false);
  }, [userSelectorPending, myCandidacies]);

  const handleSelectAllUsers = () => {
    dataAtClickRef.current = myCandidacies;
    setUserSelectorPending(ALL_USERS_KEY);
    setUserSelectorOpen(true);
    handleUpdateView({
      ...myCandidaciesQueryParams,
      userId: null,
      allUsers: true,
    });
  };

  const handleSelectUser = (userId: number) => {
    dataAtClickRef.current = myCandidacies;
    setUserSelectorPending(userId);
    setUserSelectorOpen(true);
    handleUpdateView({
      ...myCandidaciesQueryParams,
      userId,
      allUsers: false,
    });
  };

  // Compute flat ordered list (so ignoring groups) of candidacies for navigation.
  const flatList = aviary.flattenMyCandidaciesForNav(
    myCandidacies ?? [],
    myCandidaciesQueryParams.groupBy,
  );

  // Find the index of the selected candidacy in the flat list.
  const currentIndex = selectedCandidacyId
    ? flatList.findIndex((c) => c.candidacyId === selectedCandidacyId)
    : -1;
  const inListRow = currentIndex >= 0 ? flatList[currentIndex] : null;

  // Initialize nav on open from url
  useEffect(() => {
    if (selectedCandidacyId && !previousCandidacyId && !nextCandidacyId) {
      const idx = flatList.findIndex(
        (c) => c.candidacyId === selectedCandidacyId,
      );
      if (idx !== -1) {
        setPreviousCandidacyId(flatList[idx - 1]?.candidacyId ?? null);
        setNextCandidacyId(flatList[idx + 1]?.candidacyId ?? null);
      }
    }
  }, [flatList, nextCandidacyId, previousCandidacyId, selectedCandidacyId]);

  // Set previous and next candidacy ids when the selected candidacy changes
  const setSelectedCandidacyById = (candidacyId: string | null) => {
    if (!candidacyId) {
      setPreviousCandidacyId(null);
      setSelectedCandidacyId(null);
      setNextCandidacyId(null);
      return;
    }
    setSelectedCandidacyId(candidacyId);
    const idx = flatList.findIndex((c) => c.candidacyId === candidacyId);
    if (idx !== -1) {
      setPreviousCandidacyId(flatList[idx - 1]?.candidacyId ?? null);
      setNextCandidacyId(flatList[idx + 1]?.candidacyId ?? null);
    }
  };

  const handleMoveToNextCandidacy = () => {
    if (!nextCandidacyId) return;
    const idx = flatList.findIndex((c) => c.candidacyId === nextCandidacyId);
    if (idx === -1) {
      throw new Error(
        `nextCandidacyId ${nextCandidacyId} is defined but the candidacy cannot be found`,
      );
    }
    setPreviousCandidacyId(flatList[idx - 1]?.candidacyId ?? null);
    setSelectedCandidacyId(nextCandidacyId);
    setNextCandidacyId(flatList[idx + 1]?.candidacyId ?? null);
  };
  const handleMoveToPreviousCandidacy = () => {
    if (!previousCandidacyId) return;
    const idx = flatList.findIndex(
      (c) => c.candidacyId === previousCandidacyId,
    );
    if (idx === -1) {
      throw new Error(
        `previousCandidacyId ${previousCandidacyId} is defined but the candidacy cannot be found`,
      );
    }
    setPreviousCandidacyId(flatList[idx - 1]?.candidacyId ?? null);
    setSelectedCandidacyId(previousCandidacyId);
    setNextCandidacyId(flatList[idx + 1]?.candidacyId ?? null);
  };

  // If the selected candidacy is not in the list, we need to fetch its job/company ids and redirect to the pipeline.
  const needsJobAndCompanyId =
    isMyCandidaciesSuccess && !!selectedCandidacyId && !inListRow;
  const {
    data: locator,
    isError: isLocatorError,
    isSuccess: isLocatorSuccess,
  } = useGetJobAndCompanyIdForCandidacy({
    candidacyId: selectedCandidacyId,
    enabled: needsJobAndCompanyId,
  });

  useEffect(() => {
    if (!needsJobAndCompanyId) return;

    if (isLocatorError) {
      errorToast({
        description:
          "Candidacy not found. The link may be invalid or the candidacy may have been removed.",
      });
      setSelectedCandidacyById(null);
      return;
    }

    if (isLocatorSuccess) {
      // Clear the aviary-side c= before navigating
      setSelectedCandidacyById(null);
      void navigate(
        `/companies/${locator.companyId}/jobs/${locator.jobId}?c=${locator.candidacyId}`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSelectedCandidacyById is redefined each render; we only want to fire on locator status changes.
  }, [
    errorToast,
    isLocatorError,
    isLocatorSuccess,
    locator,
    navigate,
    needsJobAndCompanyId,
  ]);

  const userId = inListRow?.talentId;

  const render = () => {
    if (isMyCandidaciesError || isViewsError) {
      return (
        <Center className="h-full">
          <ErrorText
            errorMessage={
              "An error occurred while loading candidacies or views. Please let the engineers know if they are not already on it!"
            }
          />
        </Center>
      );
    }

    // selectedView is redundant but needed for TypeScript narrowing below.
    if (
      isMyCandidaciesLoading ||
      isViewsLoading ||
      !isMyCandidaciesSuccess ||
      !isViewsSuccess ||
      !selectedView
    ) {
      return (
        <Center className="h-full">
          <LoadingIndicator variant="xlarge" />
        </Center>
      );
    }

    return (
      <Column className="gap-4 p-6">
        <TableViews
          defaultQueryParams={defaultMyCandidaciesQueryParams}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          tableName={ViewTableName.MY_CANDIDACIES}
          viewQueryParams={viewQueryParams}
          views={views}
        />
        <MyAviaryTableQueryOptions
          myCandidaciesQueryParams={myCandidaciesQueryParams}
          handleUpdateView={handleUpdateView}
          userSelectorDisplayedQueryParams={myCandidaciesQueryParams}
          userSelectorOpen={userSelectorOpen}
          userSelectorPendingSelection={userSelectorPending}
          onUserSelectorOpenChange={setUserSelectorOpen}
          onSelectAllUsers={handleSelectAllUsers}
          onSelectUser={handleSelectUser}
        />
        <div className="max-h-[calc(100vh-10rem)] overflow-auto">
          <MyAviaryTable
            groupBy={myCandidaciesQueryParams.groupBy}
            handleUpdateView={handleUpdateView}
            myCandidaciesQueryParams={myCandidaciesQueryParams}
            myCandidacies={myCandidacies}
            setSelectedCandidacyId={setSelectedCandidacyById}
          />
        </div>
        {inListRow && userId && (
          <MyAviaryCandidateSheet
            candidacyId={inListRow.candidacyId}
            canMoveToNextCandidacy={!!nextCandidacyId}
            canMoveToPreviousCandidacy={!!previousCandidacyId}
            companyId={inListRow.companyId}
            currentCandidacyIndex={currentIndex}
            handleMoveToNextCandidacy={handleMoveToNextCandidacy}
            handleMoveToPreviousCandidacy={handleMoveToPreviousCandidacy}
            jobId={inListRow.jobId}
            setSelectedCandidacyId={setSelectedCandidacyById}
            totalCandidaciesCount={flatList.length}
            userId={userId}
          />
        )}
      </Column>
    );
  };

  return render();
};
